import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Zap, User, Award, LogIn, LogOut, Info, Users, BookOpen, Shield } from 'lucide-react';
import { useModal } from '../context/ModalContext';
import { useAuth } from '../context/AuthContext';

const BurgerMenu = ({ onProfileClick, onPointsClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const modal = useModal();
  const { isAuthenticated, user, logout } = useAuth();

  const handleDiary = () => {
    setIsOpen(false);
    if (onProfileClick) onProfileClick();
    else modal.openProfile();
  };

  const handlePoints = () => {
    setIsOpen(false);
    if (onPointsClick) onPointsClick();
    else modal.openPoints();
  };

  const handleSignOut = async () => {
    setIsOpen(false);
    await logout();
  };

  const handleSignIn = () => {
    setIsOpen(false);
    modal.openAuth();
  };

  const menuItems = [
    {
      label: 'Start Wavelength',
      icon: Zap,
      onClick: () => { setIsOpen(false); navigate('/wavelength/new'); },
      testId: 'menu-wavelength',
    },
    { label: 'About', icon: Info, onClick: () => { setIsOpen(false); navigate('/about'); }, testId: 'menu-about' },
    isAuthenticated && {
      label: 'My Profile', icon: User,
      onClick: () => { setIsOpen(false); navigate('/profile'); },
      testId: 'menu-profile-page',
    },
    isAuthenticated && user?.is_org_member && {
      label: 'Team Directory', icon: Users,
      onClick: () => { setIsOpen(false); navigate('/directory'); },
      testId: 'menu-directory',
    },
    isAuthenticated && (user?.role === 'admin' || user?.role === 'super_admin') && {
      label: 'Team Admin', icon: Shield,
      onClick: () => { setIsOpen(false); navigate('/team'); },
      testId: 'menu-team-admin',
    },
    { label: 'Points',  icon: Award, onClick: handlePoints, testId: 'menu-points' },
    isAuthenticated
      ? { label: 'Sign Out', icon: LogOut, onClick: handleSignOut, testId: 'menu-sign-out' }
      : { label: 'Sign In', icon: LogIn, onClick: handleSignIn, testId: 'menu-sign-in' },
  ].filter(Boolean);

  return (
    <div className="relative">
      {/* Burger button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg transition-colors duration-200 hover:bg-orange-primary/10"
        data-testid="burger-menu-button"
        aria-label="Menu"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X size={24} className="text-charcoal" />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Menu size={24} className="text-charcoal" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Dropdown menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Menu panel */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute right-0 top-full mt-2 z-50 min-w-[200px]"
            >
              <div 
                className="rounded-xl overflow-hidden shadow-xl border border-charcoal/10"
                style={{
                  background: 'linear-gradient(145deg, rgba(255,252,248,0.98), rgba(255,245,235,0.95))',
                  backdropFilter: 'blur(12px)',
                }}
              >
                {menuItems.map((item, index) => (
                  <motion.button
                    key={item.label}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={item.onClick}
                    className="w-full px-4 py-3 flex items-center gap-3 text-left transition-[background-color,color] duration-200 hover:bg-orange-primary/10 text-charcoal"
                    data-testid={item.testId}
                  >
                    <item.icon size={18} className="text-orange-primary/70" />
                    <span className="font-medium">{item.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BurgerMenu;
